import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import {
  runnerApi, errorData, errorText, type MarkOutcome, type Scan, type ScanItem, type StatusGrid, type Verdict,
} from '../../../../api/migrationRunner';
import { Btn, Dialog, ErrorBox, Pill, Spinner, when, type Tone } from '../ui';
import { BulkMarkDialog, PairList, migName, plural, skippedText, today, type CleanupCtx } from './shared';

const keyOf = (i: { scriptId: number; targetDb: string }) => `${i.scriptId}|${i.targetDb}`;

const folderKey = (folder: string) => {
  const m = /^migrat(?:e|ions?)-(\d{4})-(\d{1,2})-(\d{1,2})/.exec(folder);
  return m ? Number(m[1]) * 10000 + Number(m[2]) * 100 + Number(m[3]) : null;
};
const isoDay = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

interface GroupDef {
  verdict: Verdict;
  title: string;
  sub: string;
  tone: Tone;
  color: string;
  legend: string;
}

const GROUPS: GroupDef[] = [
  { verdict: 'inplace', title: 'Already in place', sub: 'Every statement would do nothing. Mark them done and they stop showing as pending.', tone: 'ok', color: 'bg-green-600', legend: 'already in place' },
  { verdict: 'needed', title: 'Still needed', sub: 'These would really change something.', tone: 'info', color: 'bg-blue-600', legend: 'still needed' },
  { verdict: 'check', title: 'Data fixes: check', sub: 'UPDATE/DELETE only. The scan counts how many rows still match today; INSERTs it can only show you.', tone: 'warn', color: 'bg-amber-500', legend: 'data fixes, check' },
  { verdict: 'obsolete', title: 'Out of date', sub: 'A later migration already changed what this one touches. Running it would fail.', tone: 'mut', color: 'bg-slate-400', legend: 'out of date' },
  { verdict: 'unknown', title: "Can't tell", sub: "Triggers, procedures, views or changes the dry run doesn't read, or a database that didn't answer. Look at these yourself.", tone: 'vio', color: 'bg-violet-400', legend: "can't tell" },
];

/** Default selection: everything, except data fixes that still match rows or couldn't be counted. */
const defaultOn = (i: ScanItem) => i.verdict !== 'check' || i.rows === 0;

/** Clean up › Pending (approved mockup screen 3): scan, grouped verdicts, bulk actions. */
const PendingView: React.FC<{ ctx: CleanupCtx; refreshKey: number }> = ({ ctx, refreshKey }) => {
  const [scan, setScan] = useState<Scan | null>(null);
  const [grid, setGrid] = useState<StatusGrid | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [sel, setSel] = useState<Record<string, boolean>>({});
  const [done, setDone] = useState<Set<string>>(new Set());
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const [bulk, setBulk] = useState<{ verdict: Verdict; items: ScanItem[] } | null>(null);
  const poll = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [clearUpTo, setClearUpTo] = useState(() => isoDay(new Date(new Date().getFullYear(), new Date().getMonth(), 0)));
  const [clearOpen, setClearOpen] = useState(false);
  const [clearing, setClearing] = useState(false);

  const follow = useCallback((s: Scan) => {
    setScan(s);
    if (poll.current) clearTimeout(poll.current);
    if (s.status === 'running') {
      poll.current = setTimeout(async () => {
        try { follow(await runnerApi.scan(s.id)); } catch (err) { setError(errorText(err)); }
      }, 2000);
    }
  }, []);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    Promise.all([
      runnerApi.latestScan('pending').catch((err) => {
        // Not set up yet: the tab's banner says so.
        if (alive && errorData(err)?.code !== 'CLEANUP_SCHEMA_MISSING') setError(errorText(err));
        return null;
      }),
      runnerApi.status().catch(() => null),
    ]).then(([s, g]) => {
      if (!alive) return;
      setGrid(g);
      if (s) follow(s);
      setDone(new Set());
      setSel({});
    }).finally(() => alive && setLoading(false));
    return () => { alive = false; if (poll.current) clearTimeout(poll.current); };
  }, [follow, refreshKey]);

  const start = async () => {
    setError(null);
    try {
      setDone(new Set());
      setSel({});
      follow(await runnerApi.startScan('pending'));
    } catch (err) {
      setError(errorText(err, 'Could not start the scan'));
    }
  };

  const items = useMemo(() => (scan?.status === 'done' ? (scan.result?.items ?? []).filter((i) => !done.has(keyOf(i))) : []), [scan, done]);
  const byVerdict = useMemo(() => {
    const m = new Map<Verdict, ScanItem[]>();
    for (const i of items) {
      if (!m.has(i.verdict)) m.set(i.verdict, []);
      m.get(i.verdict)!.push(i);
    }
    return m;
  }, [items]);
  const isOn = (i: ScanItem) => sel[keyOf(i)] ?? defaultOn(i);

  // Pending cells before any scan (from the status grid).
  const pendingCells = useMemo(() => {
    if (!grid) return [];
    const out: { key: string; folderPath: string; companyName: string; dateKey: number | null }[] = [];
    const names = new Map(grid.targets.map((t) => [t.targetDb, t.name]));
    for (const m of grid.migrations) {
      for (const [db, cell] of Object.entries(grid.cells[m.scriptId] ?? {})) {
        if (cell.state === 'not_run') out.push({ key: `${m.scriptId}|${db}`, folderPath: m.folderPath, companyName: names.get(db) || db, dateKey: folderKey(m.folder) });
      }
    }
    return out;
  }, [grid]);

  const bulkCfg = (v: Verdict, n: number): { title: string; sub: string; note: string; cta: string; outcome: MarkOutcome; choose?: boolean } => {
    const d = today();
    switch (v) {
      case 'inplace': return { title: `Mark ${n} done`, sub: 'Recorded as resolved: found already in place. Nothing runs.', note: `Found already in place by scan (all statements were no-ops), ${d}.`, cta: 'Mark done', outcome: 'found_in_place' };
      case 'check': return { title: `Mark ${n} not needed`, sub: '0 rows still match these, so the fix already happened (check the ones that still match before you tick them).', note: `Data fix already done: 0 rows match today (scan ${d}).`, cta: 'Mark not needed', outcome: 'not_needed' };
      case 'obsolete': return { title: `Mark ${n} not needed`, sub: 'A later migration replaced these.', note: `Out of date: replaced by a later migration (scan ${d}).`, cta: 'Mark not needed', outcome: 'not_needed' };
      default: return { title: `Mark ${n}`, sub: "The scan couldn't tell. Only mark these if you know.", note: '', cta: 'Save', outcome: 'applied_by_hand', choose: true };
    }
  };

  const act = (v: Verdict) => {
    const list = (byVerdict.get(v) ?? []).filter(isOn);
    if (!list.length) return;
    if (v === 'needed') {
      ctx.nav.openBatchDialog({
        migrationIds: [...new Set(list.map((i) => i.scriptId))],
        targets: [...new Set(list.map((i) => i.targetDb))],
        onlyPairs: list.map(keyOf),
        title: `Apply ${plural(list.length, 'pending migration')}`,
      });
      return;
    }
    setBulk({ verdict: v, items: list });
  };

  const confirmBulk = async (note: string, outcome: MarkOutcome) => {
    if (!bulk) return;
    const pairs = bulk.items.map((i) => ({ scriptId: i.scriptId, targetDb: i.targetDb }));
    const r = await ctx.mark(pairs, outcome, note, (res) => `Marked ${plural(res.marked, 'pair')} ${outcome === 'not_needed' ? 'not needed' : 'done'}.`);
    // Hide what was marked (and what turned out to be done already); keep the rest visible.
    const kept = new Set(r.skipped.filter((x) => x.reason !== 'already_done' && x.scriptId).map((x) => `${x.scriptId}|${x.targetDb}`));
    setDone((prev) => new Set([...prev, ...bulk.items.map(keyOf).filter((k) => !kept.has(k))]));
    setBulk(null);
  };

  const [cy, cm, cd] = clearUpTo.split('-').map(Number);
  const clearKey = cy && cm && cd ? cy * 10000 + cm * 100 + cd : null;
  const toClear = clearKey ? pendingCells.filter((p) => p.dateKey !== null && p.dateKey <= clearKey) : [];
  const clearAll = async () => {
    if (!clearKey) return;
    setClearing(true);
    setError(null);
    try {
      const r = await runnerApi.clearPending(`${cy}-${cm}-${cd}`);
      setClearOpen(false);
      const markId = r.markId;
      ctx.notify(`Cleared ${plural(r.marked, 'pending pair')} as not needed.${skippedText({ ...r, inserted: 0, updated: 0 })}`,
        markId ? async () => { await runnerApi.undoMark(markId); ctx.notify('Undone.'); ctx.changed(); } : undefined);
      ctx.changed();
    } catch (err) {
      setError(errorText(err, 'Could not clear'));
    } finally {
      setClearing(false);
    }
  };

  const summary = scan?.result?.summary;
  const counts = Object.fromEntries(GROUPS.map((g) => [g.verdict, byVerdict.get(g.verdict)?.length ?? 0])) as Record<Verdict, number>;
  const total = items.length || 1;
  const running = scan?.status === 'running';

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-[15px] font-bold text-slate-900">Pending migrations</h2>
          <p className="text-[12.5px] text-slate-500">
            {plural(pendingCells.length, 'migration × database pair')} never ran. The scan does a dry run of every one against that database's live tables. Nothing is changed.
            {scan?.status === 'done' && <> Last scan {when(scan.finishedAt)} by {scan.createdBy}.</>}
          </p>
        </div>
        {running ? (
          <div className="flex items-center gap-2 text-[12.5px] text-slate-500">
            <Spinner /> Checking {scan!.progress.done} of {scan!.progress.total}{scan!.progress.current ? ` (${scan!.progress.current})` : ''}…
          </div>
        ) : (
          <Btn kind={scan?.status === 'done' ? 'ghost' : 'primary'} small={scan?.status === 'done'} onClick={start} disabled={!ctx.ready || !ctx.isOwner}>
            <MagnifyingGlassIcon className="h-4 w-4" /> {scan?.status === 'done' ? 'Scan again' : "Scan what's really needed"}
          </Btn>
        )}
      </div>

      {pendingCells.length > 0 && (
        <div className="flex flex-wrap items-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-3 text-[13px]">
          <span className="min-w-0 flex-1 text-slate-600">Old and no longer needed? Clear everything pending up to</span>
          <input type="date" value={clearUpTo} onChange={(e) => setClearUpTo(e.target.value)} className="rounded-lg border border-slate-300 px-2 py-1 text-[12.5px]" />
          <Btn kind="danger" small disabled={!toClear.length || !ctx.isOwner || !ctx.ready} onClick={() => setClearOpen(true)}>Clear {toClear.length}</Btn>
        </div>
      )}
      {error && <ErrorBox>{error}</ErrorBox>}
      {scan?.status === 'failed' && <ErrorBox>The last scan failed: {scan.error}</ErrorBox>}
      {loading && <div className="flex items-center gap-2 text-sm text-slate-500"><Spinner /> Loading…</div>}

      {!loading && scan?.status !== 'done' && (
        <section className="overflow-hidden rounded-xl border border-slate-200 bg-white">
          <ul className="max-h-[480px] divide-y divide-slate-100 overflow-auto">
            {pendingCells.slice(0, 300).map((p) => (
              <li key={p.key} className="flex items-center justify-between gap-3 px-4 py-2.5 text-[13px]">
                <span className="min-w-0"><b className="font-mono text-[12px]">{migName(p.folderPath)}</b> <span className="text-slate-500">on {p.companyName}</span></span>
                <Pill tone="mut">pending</Pill>
              </li>
            ))}
            {pendingCells.length > 300 && <li className="px-4 py-2.5 text-[13px] text-slate-500">and {pendingCells.length - 300} more</li>}
            {!pendingCells.length && <li className="px-4 py-4 text-[13px] text-slate-500">Nothing pending.</li>}
          </ul>
        </section>
      )}

      {scan?.status === 'done' && summary && (
        <>
          <div className="space-y-2 rounded-xl border border-slate-200 bg-white p-4">
            <div className="flex h-2 overflow-hidden rounded-full bg-slate-100">
              {GROUPS.map((g) => <i key={g.verdict} className={`block h-full ${g.color}`} style={{ width: `${(counts[g.verdict] / total) * 100}%` }} />)}
            </div>
            <div className="flex flex-wrap gap-x-4 gap-y-1 text-[12.5px] text-slate-600">
              {GROUPS.map((g) => (
                <span key={g.verdict} className="inline-flex items-center gap-1.5"><i className={`inline-block h-2 w-2 rounded-full ${g.color}`} />{counts[g.verdict]} {g.legend}</span>
              ))}
            </div>
          </div>
          {GROUPS.map((g) => {
            const list = byVerdict.get(g.verdict) ?? [];
            if (!list.length) return null;
            const on = list.filter(isOn).length;
            const show = expanded[g.verdict] ? list : list.slice(0, 100);
            const label = g.verdict === 'needed' ? `Apply ${on}` : g.verdict === 'inplace' ? `Mark ${on} done` : g.verdict === 'unknown' ? `Mark ${on}…` : `Mark ${on} not needed`;
            return (
              <section key={g.verdict} className="overflow-hidden rounded-xl border border-slate-200 bg-white">
                <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-200 px-4 py-3">
                  <div className="min-w-0 flex-1">
                    <b className="text-slate-900">{g.title}</b> <Pill tone={g.tone}>{list.length}</Pill>
                    <div className="text-[12.5px] text-slate-500">{g.sub}</div>
                  </div>
                  <div className="flex items-center gap-2">
                    <button type="button" className="text-[12px] font-semibold text-blue-600 hover:underline" onClick={() => setSel((s) => ({ ...s, ...Object.fromEntries(list.map((i) => [keyOf(i), on < list.length])) }))}>
                      {on < list.length ? 'Select all' : 'Select none'}
                    </button>
                    <Btn small kind={g.verdict === 'needed' ? 'primary' : 'success'} disabled={!on || !ctx.isOwner || (g.verdict !== 'needed' && !ctx.ready)} onClick={() => act(g.verdict)}>{label}</Btn>
                  </div>
                </div>
                <ul className="divide-y divide-slate-100">
                  {show.map((i) => (
                    <li key={keyOf(i)} className="grid grid-cols-[22px_minmax(0,1fr)_auto] items-start gap-3 px-4 py-2.5">
                      <input
                        type="checkbox"
                        className="mt-1"
                        checked={isOn(i)}
                        onChange={(e) => setSel((s) => ({ ...s, [keyOf(i)]: e.target.checked }))}
                        aria-label={`Select ${i.folderPath} on ${i.companyName}`}
                      />
                      <div className="min-w-0 text-[13px]">
                        <b className="font-mono text-[12px]">{migName(i.folderPath)}</b> <span className="text-slate-500">on {i.companyName}</span>
                        {i.state !== 'not_run' && <> <Pill tone="mut">{i.state}</Pill></>}
                        <div className="text-[12.5px] text-slate-500">{i.detail}</div>
                      </div>
                      <Btn kind="ghost" small onClick={() => ctx.nav.openPreview(i.scriptId)}>Preview</Btn>
                    </li>
                  ))}
                  {list.length > show.length && (
                    <li className="px-4 py-2.5"><button type="button" className="text-[12.5px] font-semibold text-blue-600 hover:underline" onClick={() => setExpanded((e) => ({ ...e, [g.verdict]: true }))}>Show all {list.length}</button></li>
                  )}
                </ul>
              </section>
            );
          })}
          {!items.length && <div className="rounded-xl border border-slate-200 bg-white p-4 text-[13px] text-slate-500">Nothing left from this scan. Scan again to check what's pending now.</div>}
        </>
      )}

      <Dialog
        open={clearOpen}
        onClose={() => setClearOpen(false)}
        title={`Clear ${plural(toClear.length, 'pending pair')} up to ${clearUpTo}`}
        footer={<><Btn kind="ghost" onClick={() => setClearOpen(false)}>Cancel</Btn><Btn kind="danger" onClick={clearAll} disabled={clearing}>{clearing ? 'Clearing…' : 'Clear all as not needed'}</Btn></>}
      >
        <p>Every migration from a folder dated on or before {clearUpTo} that never ran on a database is recorded as <b>not needed</b> (cleared, no longer needed), in one mark. Nothing runs. Undo from the toast or History for 30 days.</p>
        <PairList items={toClear} />
      </Dialog>

      {bulk && (() => {
        const cfg = bulkCfg(bulk.verdict, bulk.items.length);
        return (
          <BulkMarkDialog
            open
            onClose={() => setBulk(null)}
            title={cfg.title}
            sub={cfg.sub}
            items={bulk.items.map((i) => ({ key: keyOf(i), folderPath: i.folderPath, companyName: i.companyName }))}
            note={cfg.note}
            cta={cfg.cta}
            choose={cfg.choose}
            outcome={cfg.outcome}
            onConfirm={confirmBulk}
          />
        );
      })()}
    </div>
  );
};

export default PendingView;
